import { useResumenLogistica, type ResumenLogistica } from '../../hooks/useResumenLogistica'
import {
  IconoCalendario,
  IconoUbicacion,
  IconoTelefono,
  IconoAviso,
  IconoConfirmar,
} from '../../componentes/iconos'

type Tono = 'acento' | 'positivo' | 'atencion' | 'critico'

const BARRA_LATERAL: Record<Tono, string> = {
  acento: 'before:bg-acento',
  positivo: 'before:bg-positivo-tinta',
  atencion: 'before:bg-atencion-tinta',
  critico: 'before:bg-critico-tinta',
}
const RELLENO: Record<Tono, string> = {
  acento: 'bg-acento',
  positivo: 'bg-positivo-tinta',
  atencion: 'bg-atencion-tinta',
  critico: 'bg-critico-tinta',
}

/**
 * Panel de métricas de Logística: franja superior siempre visible. Diseño de
 * tarjetas compactas con barra de color lateral (estado) + un panel destacado
 * de "Carga por día" de la semana. Métricas: carga operativa, calidad de datos
 * y seguimiento comercial. Solo iconos, sin emojis.
 */
export function PanelMetricasLogistica() {
  const { data, isPending, isError } = useResumenLogistica()

  // Si falla, no estorbamos la pantalla: la tabla de Logística sigue funcionando.
  if (isError) return null

  return (
    <section aria-label="Métricas de logística" className="flex flex-col gap-3">
      <div className="grid gap-3 lg:grid-cols-[1.3fr_1fr]">
        <PanelCargaDia datos={data} cargando={isPending} />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-2">
          <Tarjeta
            tono="positivo"
            valor={isPending ? '—' : `${data?.porcentajeGeocodificados ?? 0}%`}
            titulo="Geocodificados"
            detalle={isPending ? '' : `${data?.geocodificados ?? 0} de ${data?.activos ?? 0} en el mapa`}
            progreso={data?.porcentajeGeocodificados}
            icono={<IconoUbicacion className="size-4" />}
          />
          <Tarjeta
            tono="critico"
            valor={isPending ? '—' : String(data?.enRiesgoFuga ?? 0)}
            titulo="En riesgo de fuga"
            detalle="6+ semanas sin pedir"
            icono={<IconoAviso className="size-4" />}
          />
          <Tarjeta
            tono="atencion"
            valor={isPending ? '—' : String(data?.sinTelefono ?? 0)}
            titulo="Sin teléfono"
            detalle="no reciben WhatsApp"
            icono={<IconoTelefono className="size-4" />}
          />
          <Tarjeta
            tono="atencion"
            valor={isPending ? '—' : String(data?.sinRuta ?? 0)}
            titulo="Sin ruta asignada"
            detalle="revisar etiquetas"
            icono={<IconoConfirmar className="size-4" />}
          />
        </div>
      </div>
    </section>
  )
}

function PanelCargaDia({ datos, cargando }: { datos?: ResumenLogistica; cargando: boolean }) {
  const dias = datos?.cargaPorDia ?? []
  const maximo = Math.max(1, ...dias.map((d) => d.carga))
  return (
    <div className="rounded-2xl border border-borde bg-superficie p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-tinta-apagada">
        <IconoCalendario className="size-4 text-acento" />
        Carga por día
        <span className="font-normal normal-case text-tinta-apagada">{cargando ? '· —' : `· ${datos?.repartosSemana ?? 0} repartos`}</span>
      </h3>
      <div className="flex flex-col gap-2.5">
        {(cargando ? PLACEHOLDER : dias).map((d) => {
          const esMax = !cargando && datos?.diaMasCargado?.dia === d.dia && d.carga > 0
          const ancho = cargando ? 30 : Math.round((d.carga / maximo) * 100)
          return (
            <div key={d.ruta} className="grid grid-cols-[76px_1fr_28px] items-center gap-2.5 text-[13px]">
              <span className="capitalize text-tinta-2">{d.dia}</span>
              <span className="h-2.5 overflow-hidden rounded-full bg-elevado">
                <span
                  className={`block h-full rounded-full ${esMax ? 'bg-atencion-tinta' : 'bg-acento'} ${cargando ? 'animate-pulse' : ''}`}
                  style={{ width: `${ancho}%` }}
                />
              </span>
              <span className="text-right font-semibold tabular-nums text-tinta">{cargando ? '' : d.carga}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Tarjeta({
  tono,
  valor,
  titulo,
  detalle,
  progreso,
  icono,
}: {
  tono: Tono
  valor: string
  titulo: string
  detalle?: string
  progreso?: number
  icono: React.ReactNode
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-borde bg-superficie py-3 pl-[18px] pr-3.5 before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-[''] ${BARRA_LATERAL[tono]}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xl font-extrabold tabular-nums text-tinta">{valor}</span>
        <span className="text-tinta-apagada">{icono}</span>
      </div>
      <p className="mt-0.5 text-xs font-semibold text-tinta-apagada">{titulo}</p>
      {typeof progreso === 'number' ? (
        <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-elevado">
          <span className={`block h-full rounded-full ${RELLENO[tono]}`} style={{ width: `${progreso}%` }} />
        </span>
      ) : null}
      {detalle ? <p className="mt-1 text-[11px] text-tinta-apagada">{detalle}</p> : null}
    </div>
  )
}

const PLACEHOLDER = [
  { ruta: 'r1' as const, dia: 'lunes', carga: 0 },
  { ruta: 'r2' as const, dia: 'martes', carga: 0 },
  { ruta: 'r3' as const, dia: 'miércoles', carga: 0 },
  { ruta: 'r4' as const, dia: 'jueves', carga: 0 },
  { ruta: 'r5' as const, dia: 'viernes', carga: 0 },
]
